import type { Feature, Point } from 'geojson';
import L from 'leaflet';
import { SourceBase } from '../Sources/SourceBase';

export interface SourceLayer {
    onSourceUpdate(source: SourceBase, data: Feature[]): void;
}
export interface Filter {
    order?: number;
    filterFn: FilterFn;
}

export interface SupplimentMarkerConfig {
    latLng: L.LatLng;
    minZoom: number;
}

export interface ClusteredMarkerLayerOptions extends L.MarkerClusterGroupOptions {
    name: string;
    source: SourceBase;
    itemPlural?: string;
    disablePopup?: boolean;
    persistentInformationLayer?: boolean;
    templateName?: string | ((sourceId: string) => string);
    createInformationLayer?(feature: Feature, marker: L.Marker): L.Layer;
    createPersistentOverlay?(feature: Feature, marker: L.Marker): L.Layer | null;
    customIcon?: L.Icon<L.BaseIconOptions> | ((feature: Feature) => L.Icon<L.BaseIconOptions>);
    clusterIconUrl?: string;
    defaultIconId?: number | string;
    combinationIcon?: boolean;
    customClusterIconId?: number | string | ((markers: L.Marker[]) => number | string);
    popupTemplate?(featureProperties: unknown, layerProperties: ClusteredMarkerLayerOptions, waitingForDetails?: boolean): JSX.Element;
    onPopupOpen?(event): void;
    popupOptions?: L.PopupOptions;
    defaultFilter?: Filter;
    markerTitle?: (featureProperties: unknown) => string;
    markerPositions?: (feature: Feature<Point>) => L.LatLng[];
    splitMarkersMinDistance?: number;
    onMarkerAdd?(marker: L.Marker, feature: Feature): void;
    onMarkerClick?(event: L.LeafletMouseEvent): void;
    onMarkerMouseOver?: (event: L.LeafletMouseEvent) => void;
    onMarkerMouseOut?: (event: L.LeafletMouseEvent) => void;
}

export class InformationLayer extends L.Layer {
    priority?: number;
}

export interface RoadStatusLayerOptions extends L.GeoJSONOptions {
    source: SourceBase;
    statusSource: SourceBase;
    tooltipTemplate?(featureProperties: unknown): string | HTMLElement;
    getStatus(section): number | string;
    getStatusCode(status: number | string): number;
    color: { [key: string]: string };
    filterByZoom?(feature: Feature, zoom: number): boolean;
    filterByStatus?(feature: Feature): boolean;
    weightByZoom?(feature: Feature, zoom: number): number;
    missingStatusMeansFreeflow?: boolean;
    renderer?: L.Renderer;
}

export interface StatusData {
    id: number | string;
    status?: number | string;
    updateDate?: string;
}

export interface ClosedRoadLayerOptions extends L.LayerOptions {
    feature: Feature;
    weight?: number;
    color?: string;
    baseColor?: string;
    dashArray?: string;
}

export interface GeoJSONTrafikenOptions extends L.GeoJSONOptions {
    source: SourceBase;
    popupTemplate?(featureProperties: unknown, layerProperties: GeoJSONTrafikenOptions, waitingForDetails?: boolean): string;
    popupOptions?: L.PopupOptions;
}

export interface WinterBikeRoadsSthOptions extends GeoJSONTrafikenOptions {
    name: string;
    municipality: string;
    updated: string;
    url: string;
    baseUrl: string;
    method: string;
    frequency?: string;
}

export interface ExtentLayerOptions extends L.GeoJSONOptions {
    arrowDirection?: 'one' | 'both' | 'reverse';
    arrowPane?: string;
}

export const TRAVELTIME_PANE_410 = 'traveltime-pane-410',
    TRAVELTIME_PANE_420 = 'traveltime-pane-420',
    TRAVELTIME_PANE_430 = 'traveltime-pane-430',
    TRAVELTIME_PANE_440 = 'traveltime-pane-440',
    TRAVELTIME_PANE_450 = 'traveltime-pane-450';

export type FilterFn = (filteredFeatures: Feature[], allFeatures: Feature[]) => Feature[];

export interface FilterLayerOptions extends L.LayerOptions {
    filterFn?: FilterFn;
    inverted?: boolean;
    order?: number;
}
